import React from "react";
import { useNavigate } from "react-router-dom";
import Title from "@/components/Title";
import {
  Card,
  CardBody,
  Input,
  CloseButton,
  Stack,
  useToast,
  Switch,
  FormControl,
  FormLabel,
  Divider,
  Button,
} from "@chakra-ui/react";
import { BsPersonFill, BsVolumeUpFill } from "react-icons/bs";
import { getPlatform } from "@/lib/iap";
import {
  callWithAuth,
  deleteUser,
  postCreateCustomerPortalSession,
  getUserInfo,
} from "@/api/Api.ts";
import DeleteAccountButton, {
  DeleteAccountButtonHandle,
} from "@/components/Buttons/DeleteAccountButton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
const PLATFORM = getPlatform();
import VERSION, { DEV_ADDON } from "@/version";
import { useUserSettings } from "@/hooks/useUserSettings";
import AdminButton from "../Buttons/AdminButton";

interface SettingsProps {
  onClose: () => void;
  userEmail: string;
}

const Settings: React.FC<SettingsProps> = ({ onClose, userEmail }) => {
  const [email, setEmail] = React.useState("");
  const toast = useToast();
  const nav = useNavigate();
  const {
    settings,
    updateSettings,
    isLoading: settingsLoading,
  } = useUserSettings();
  const queryClient = useQueryClient();
  const deleteAccountCloseRef = React.useRef<DeleteAccountButtonHandle | null>(
    null
  );

  const query = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const user = query.data;

  const handleAuth = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() === "") {
      throw new Error("Email required");
    } else if (!emailRegex.test(email)) {
      throw new Error("Invalid email");
    } else if (email !== userEmail) {
      throw new Error("Entered email does not match user email");
    } else {
      return;
    }
  };

  const handleDelete = async () => {
    try {
      await callWithAuth(deleteUser);
      queryClient.invalidateQueries();
      nav("/");
    } catch (err) {
      toast({
        title: "Delete Account Failed",
        description:
          "Account deletion failed, please contact support@fluentwithparrot.com for further assistance.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
  };

  const getUrlAndRedirect = async () => {
    const response = await callWithAuth(postCreateCustomerPortalSession);
    window.location.href = response.url;
  };

  const handleEmailInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value.toLowerCase());
  };

  const handleToggleMute = async () => {
    try {
      await updateSettings({
        isSoundEffectsEnabled: !settings?.isSoundEffectsEnabled,
      });
      toast({
        title: settings?.isSoundEffectsEnabled
          ? "Sound effects off"
          : "Sound effects on",
        status: "success",
        duration: 1300,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Failed to update sound settings",
        description: "Please try again later",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <div className="flex flex-col justify-start w-full h-full gap-4 text-base">
      <div className="flex flex-row w-full justify-between">
        <Title text="Settings" />
        <CloseButton onClick={onClose} />
      </div>
      <Card className="w-full h-auto" py={5}>
        <CardBody className="flex flex-col items-start justify-start gap-3 text-start">
          <div className="flex flex-row items-start justify-start gap-3 text-start">
            <div className="text-xxl font-semibold">
              <BsVolumeUpFill />
            </div>
            <div className="text-xl font-bold">Audio</div>
          </div>
          <Stack spacing={4} width={"100%"} paddingBottom={4}>
            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <FormLabel
                htmlFor="is-sound-effects-enabled"
                mb="0"
                fontSize={{}}
              >
                Sound Effects
              </FormLabel>
              <Switch
                size="lg"
                id="is-sound-effects-enabled"
                isChecked={settings?.isSoundEffectsEnabled}
                onChange={handleToggleMute}
                isDisabled={settingsLoading}
              />
            </FormControl>
          </Stack>
          <Divider />
          {user?.stripeCustomerId !== "" && PLATFORM === "web" ? (
            <>
              <div className="flex flex-row items-start justify-start gap-3 text-start">
                <div className="text-xxl font-semibold">
                  <BsPersonFill />
                </div>
                <div className="text-xl font-bold">Subscriptions</div>
              </div>
              <Stack spacing={4} width={"100%"} paddingBottom={4}>
                <Button
                  onClick={getUrlAndRedirect}
                  rounded={"xl"}
                  background={"var(--color-grey)"}
                  textColor={"white"}
                  fontSize="var(--base-font-size)"
                  height={"var(--button-height)"}
                >
                  Manage billing
                </Button>
                <AdminButton
                  onClick={() => nav("/placement")}
                  text="Redo Placement Test"
                />
              </Stack>
              <Divider />
            </>
          ) : null}
          <div className="flex flex-row items-start justify-start gap-3 text-start">
            <div className="text-xxl font-semibold">
              <BsPersonFill />
            </div>
            <div className="text-xl font-bold">Profile</div>
          </div>
          <Stack spacing={4} width={"100%"}>
            <div>
              Confirm your account email to be able to delete your account:
            </div>
            <Input
              value={email}
              onChange={handleEmailInput}
              placeholder="Email"
            />
            <DeleteAccountButton
              onAuth={handleAuth}
              onDelete={handleDelete}
              userStripeCustomerId={user?.stripeCustomerId}
              ref={deleteAccountCloseRef}
            />
            <AdminButton
              onClick={() => nav("/placement")}
              text="Redo Placement Test"
            />
            <div className="text-xs text-[var(--color-grey)] w-full flex flex-row justify-center items-center">
              <a onClick={() => nav("/privacy")}>Privacy Policy</a>
              <span className="mx-2">|</span>
              <a onClick={() => nav("/terms")}>Terms of Service</a>
              <span className="mx-2">|</span>
              <a onClick={() => nav("/support")}>Support</a>
            </div>
            <div className="text-xs text-[var(--color-grey)]">
              {VERSION} {DEV_ADDON}
            </div>
          </Stack>
        </CardBody>
      </Card>
    </div>
  );
};

export default Settings;
